import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type SetStateAction,
} from "react"
import { createPortal } from "react-dom"
import { useSelector } from "react-redux"
import { useLocation } from "react-router-dom"
import { bodyContent } from "@/store/apis/matrixApi"
import { ContentMapContext } from "@/utils/contentMapContext"
import type { ContentMapEntry } from "@/utils/contentMapTypes"
import {
  isContentMapDebugActive,
  readContentMapInitialEnabled,
  setContentMapSessionEnabled,
} from "@/utils/contentMapDebug"
import {
  buildManifestCandidateEntry,
  contentMapDynamicManifest,
  flattenMatrixLeafPaths,
  resolveManifestPathsForRoute,
} from "@/utils/contentMapManifest"

export type { ContentMapEntry } from "@/utils/contentMapTypes"
export { ContentMapContext }

type SortOrder = "visibility" | "az" | "id" | "updated"

function areEntriesEqual(
  prev: ContentMapEntry[],
  next: ContentMapEntry[]
): boolean {
  if (prev.length !== next.length) return false
  return prev.every((entry, index) => {
    const other = next[index]
    return (
      entry.path === other.path &&
      entry.refId === other.refId &&
      entry.failed === other.failed &&
      entry.assetId === other.assetId &&
      entry.contentBody === other.contentBody &&
      entry.updated === other.updated
    )
  })
}

function getSentinelTop(path: string): number {
  const el = document.getElementById(
    `content-map-ref-${path.replace(/[^a-zA-Z0-9]/g, "-")}`
  )
  if (!el) return Infinity
  return el.getBoundingClientRect().top + window.scrollY
}

function makeSortFn(order: SortOrder) {
  return (a: ContentMapEntry, b: ContentMapEntry): number => {
    // Failed entries always sink to the bottom
    if (a.failed !== b.failed) return a.failed ? 1 : -1
    switch (order) {
      case "visibility": {
        const diff = getSentinelTop(a.path) - getSentinelTop(b.path)
        return diff !== 0 ? diff : a.path.localeCompare(b.path)
      }
      case "az":
        return a.path.localeCompare(b.path)
      case "id": {
        const ai = parseInt(a.assetId, 10)
        const bi = parseInt(b.assetId, 10)
        if (isNaN(ai) && isNaN(bi)) return a.path.localeCompare(b.path)
        if (isNaN(ai)) return 1
        if (isNaN(bi)) return -1
        return ai - bi
      }
      case "updated": {
        const au = parseInt(a.updated ?? "0", 10)
        const bu = parseInt(b.updated ?? "0", 10)
        if (!a.updated && !b.updated) return a.path.localeCompare(b.path)
        if (!a.updated) return 1
        if (!b.updated) return -1
        return bu - au // descending: most recent first
      }
    }
  }
}

export function ContentMapProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const { search, pathname } = useLocation()
  const [isEnabled, setIsEnabledState] = useState(() =>
    readContentMapInitialEnabled(search)
  )

  const setIsEnabled = useCallback((value: SetStateAction<boolean>) => {
    setIsEnabledState((prev) => {
      const next = typeof value === "function" ? value(prev) : value
      setContentMapSessionEnabled(next)
      return next
    })
  }, [])

  useEffect(() => {
    if (
      isContentMapDebugActive() ||
      new URLSearchParams(search).has("contentMap")
    ) {
      setIsEnabled(true)
    }
  }, [search, setIsEnabled])

  const mapRef = useRef(new Map<string, ContentMapEntry>())
  const [entries, setEntries] = useState<ContentMapEntry[]>([])
  const [panelIsOpen, setPanelIsOpen] = useState(true)
  const [activePath, setActivePath] = useState<string | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const counterRef = useRef(0)

  useEffect(() => {
    mapRef.current.clear()
    setEntries([])
    setActivePath(null)
    counterRef.current = 0
  }, [pathname])

  useEffect(() => {
    if (isEnabled) return
    mapRef.current.clear()
    setEntries([])
    setActivePath(null)
    counterRef.current = 0
  }, [isEnabled])

  const getOrAssignRefId = useCallback(
    (path: string): number => {
      if (!isEnabled) return 0
      const existing = mapRef.current.get(path)
      if (existing?.refId != null) return existing.refId
      const refId = ++counterRef.current
      mapRef.current.set(path, {
        path,
        assetId: "",
        contentBody: "",
        failed: false,
        refId,
      })
      return refId
    },
    [isEnabled]
  )

  const register = useCallback(
    (entry: Omit<ContentMapEntry, "refId">) => {
      if (!isEnabled) return
      const existing = mapRef.current.get(entry.path)
      const existingFields: Partial<ContentMapEntry> = existing
        ? { ...existing }
        : {}
      delete existingFields.refId
      const isRuntime = !entry.sourceKind || entry.sourceKind === "runtime"
      const merged: Omit<ContentMapEntry, "refId"> = {
        ...existingFields,
        ...entry,
        manifestReason: entry.manifestReason ?? existing?.manifestReason,
        manifestSource: entry.manifestSource ?? existing?.manifestSource,
        sourceKind: isRuntime
          ? "runtime"
          : entry.sourceKind ?? existing?.sourceKind,
      }
      const refId = merged.failed
        ? existing?.refId ?? null
        : getOrAssignRefId(entry.path)
      mapRef.current.set(entry.path, { ...merged, refId })
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        const next = Array.from(mapRef.current.values())
        setEntries((prev) => (areEntriesEqual(prev, next) ? prev : next))
      }, 0)
    },
    [getOrAssignRefId, isEnabled]
  )

  const refreshEntries = useCallback(() => {
    const next = Array.from(mapRef.current.values())
    setEntries((prev) => (areEntriesEqual(prev, next) ? prev : next))
  }, [])

  const matrixContent = useSelector(bodyContent)

  // Pre-seed manifest candidates for dynamic paths not rendered on the current visit
  useEffect(() => {
    if (!isEnabled || !matrixContent) return
    const knownRoutes = flattenMatrixLeafPaths(matrixContent)
    const candidates = resolveManifestPathsForRoute(
      pathname,
      contentMapDynamicManifest,
      knownRoutes
    )
    for (const candidate of candidates) {
      register(buildManifestCandidateEntry(candidate, matrixContent))
    }
  }, [pathname, matrixContent, isEnabled, register])

  const contextValue = useMemo(
    () => ({
      isEnabled,
      setIsEnabled,
      register,
      getOrAssignRefId,
      refreshEntries,
      entries,
      panelIsOpen,
      setPanelIsOpen,
      activePath,
      setActivePath,
    }),
    [
      isEnabled,
      setIsEnabled,
      register,
      getOrAssignRefId,
      refreshEntries,
      entries,
      panelIsOpen,
      activePath,
    ]
  )

  return (
    <ContentMapContext.Provider value={contextValue}>
      {children}
    </ContentMapContext.Provider>
  )
}

function pathToAnchorId(path: string) {
  return `content-map-ref-${path.replace(/[^a-zA-Z0-9]/g, "-")}`
}

export function ContentMapAnchor({ path }: { refId: number; path: string }) {
  const map = useContext(ContentMapContext)
  const sentinelRef = useRef<HTMLSpanElement>(null)
  const [overlayRect, setOverlayRect] = useState<DOMRect | null>(null)

  const isActive = map?.activePath === path

  // When this sentinel mounts (e.g. a conditionally-rendered component becomes
  // visible), tell the panel to re-sort so the entry moves from hidden → visible.
  useLayoutEffect(() => {
    map?.refreshEntries()
  }, [])

  useLayoutEffect(() => {
    if (!isActive) {
      setOverlayRect(null)
      return
    }
    const update = () => {
      const el = sentinelRef.current
      if (!el) return
      // Walk up to find the nearest ancestor with meaningful dimensions
      let cur: HTMLElement | null = el.parentElement
      while (cur) {
        if (cur.offsetWidth > 10 && cur.offsetHeight > 10) break
        cur = cur.parentElement
      }
      if (cur) setOverlayRect(cur.getBoundingClientRect())
    }
    update()
    document.addEventListener("scroll", update, {
      passive: true,
      capture: true,
    })
    window.addEventListener("resize", update)
    return () => {
      document.removeEventListener("scroll", update, { capture: true })
      window.removeEventListener("resize", update)
    }
  }, [isActive])

  return (
    <>
      {/* Zero-size sentinel — safe inside <a>, headings, etc. */}
      <span
        ref={sentinelRef}
        id={pathToAnchorId(path)}
        style={{
          display: "inline-block",
          width: 0,
          height: 0,
          position: "absolute",
        }}
      />
      {isActive &&
        overlayRect &&
        createPortal(
          <div
            className="content-map__overlay"
            style={{
              position: "fixed",
              top: overlayRect.top,
              left: overlayRect.left,
              width: overlayRect.width,
              height: overlayRect.height,
              pointerEvents: "none",
              zIndex: 9997,
            }}
          />,
          document.body
        )}
    </>
  )
}

function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

function highlightText(text: string, term: string) {
  if (!term.trim()) return text
  const parts = text.split(
    new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi")
  )
  return parts.map((part, i) =>
    part.toLowerCase() === term.trim().toLowerCase() ? (
      <mark key={i} className="content-map__highlight">
        {part}
      </mark>
    ) : (
      part
    )
  )
}

/** Matrix CMS edit screen; uses current page origin so local/UAT/prod links stay on the same host. */
function matrixAdminEditUrlPrefix(): string {
  if (typeof window === "undefined") {
    return "https://www.gesb.wa.gov.au/_admin/?screen=contents&assetid="
  }
  // Check host for UAT/MOL patterns and adjust admin edit prefix accordingly
  const { origin, host } = window.location
  if (host.includes("mol-uat")) {
    return `https://mol-uat.gesb.wa.gov.au/_admin/?screen=contents&assetid=`
  }
  if (host.includes("mol")) {
    return `https://mol.uat.gesb.wa.gov.au/_admin/?screen=contents&assetid=`
  }
  return `${origin}/_admin/?screen=contents&assetid=`
}

export function ContentMapPanel() {
  const context = useContext(ContentMapContext)
  const adminEditPrefix = matrixAdminEditUrlPrefix()
  const [search, setSearch] = useState("")
  const [sortOrder, setSortOrder] = useState<SortOrder>("visibility")
  const [panelHeight, setPanelHeight] = useState(300)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const drag = useRef({ active: false, startY: 0, startH: 0 })
  const bodyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!drag.current.active) return
      const delta = drag.current.startY - e.clientY
      setPanelHeight(
        Math.max(
          80,
          Math.min(window.innerHeight * 0.85, drag.current.startH + delta)
        )
      )
    }
    const onUp = () => {
      drag.current.active = false
    }
    document.addEventListener("mousemove", onMove)
    document.addEventListener("mouseup", onUp)
    return () => {
      document.removeEventListener("mousemove", onMove)
      document.removeEventListener("mouseup", onUp)
    }
  }, [])

  if (!context?.isEnabled) return null

  const { entries, panelIsOpen, setPanelIsOpen, setActivePath } = context

  const onResizeStart = (e: React.MouseEvent) => {
    drag.current = { active: true, startY: e.clientY, startH: panelHeight }
    e.preventDefault()
  }

  const successCount = entries.filter((e) => !e.failed).length
  const failedCount = entries.filter((e) => e.failed).length
  const uatAssetCount = entries.filter((e) => !e.failed && !!e.assetId).length
  const prodAssetCount = entries.filter(
    (e) => !e.failed && !!e.prodAssetId
  ).length

  const filtered =
    search.trim() === ""
      ? entries
      : entries.filter(
          (e) =>
            stripHtml(e.contentBody)
              .toLowerCase()
              .includes(search.trim().toLowerCase()) ||
            e.path.toLowerCase().includes(search.trim().toLowerCase())
        )

  const sorted = [...filtered].sort(makeSortFn(sortOrder))
  const visibleSorted = sorted.filter(
    (e) => getSentinelTop(e.path) !== Infinity
  )
  const hiddenSorted = sorted
    .filter((e) => getSentinelTop(e.path) === Infinity)
    .sort((a, b) => {
      const aManifest =
        a.sourceKind === "manifest" || a.sourceKind === "manifest_pattern"
      const bManifest =
        b.sourceKind === "manifest" || b.sourceKind === "manifest_pattern"
      if (aManifest !== bManifest) return aManifest ? -1 : 1
      return a.path.localeCompare(b.path)
    })

  const activateIndex = (index: number | null) => {
    setActiveIndex(index)
    const path = index != null ? visibleSorted[index]?.path ?? null : null
    setActivePath(path)

    if (path) {
      // Scroll the page to the sentinel
      const sentinel = document.getElementById(
        `content-map-ref-${path.replace(/[^a-zA-Z0-9]/g, "-")}`
      )
      if (sentinel)
        sentinel.scrollIntoView({ behavior: "smooth", block: "center" })

      // Scroll the active row into view inside the panel body
      requestAnimationFrame(() => {
        const row = bodyRef.current?.querySelector<HTMLElement>(
          `[data-content-map-path="${path.replace(/"/g, '\\"')}"]`
        )
        if (!row || !bodyRef.current) return
        const rowRect = row.getBoundingClientRect()
        const bodyRect = bodyRef.current.getBoundingClientRect()
        const relTop = rowRect.top - bodyRect.top + bodyRef.current.scrollTop
        const relBottom = relTop + row.offsetHeight
        if (relTop < bodyRef.current.scrollTop) {
          bodyRef.current.scrollTo({ top: relTop - 4, behavior: "smooth" })
        } else if (
          relBottom >
          bodyRef.current.scrollTop + bodyRef.current.clientHeight
        ) {
          bodyRef.current.scrollTo({
            top: relBottom - bodyRef.current.clientHeight + 4,
            behavior: "smooth",
          })
        }
      })
    }
  }

  // Reset active when search changes
  const handleSearchChange = (value: string) => {
    setSearch(value)
    activateIndex(null)
  }

  const handleSortChange = (value: SortOrder) => {
    setSortOrder(value)
    activateIndex(null)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (visibleSorted.length === 0) return
    if (e.key === "ArrowDown") {
      e.preventDefault()
      activateIndex(
        activeIndex == null
          ? 0
          : Math.min(visibleSorted.length - 1, activeIndex + 1)
      )
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      activateIndex(
        activeIndex == null
          ? visibleSorted.length - 1
          : Math.max(0, activeIndex - 1)
      )
    } else if (e.key === "Escape") {
      activateIndex(null)
    }
  }

  const titleBlock = (
    <span className="content-map__title">
      Content Map{" "}
      <span className="content-map__count">
        ({successCount} assets
        {failedCount > 0 && (
          <span className="content-map__count--failed">
            {" "}
            · {failedCount} missing
          </span>
        )}
        )
      </span>
    </span>
  )

  return (
    <div
      className={`content-map${panelIsOpen ? " content-map--open" : " content-map--closed"}`}
      style={panelIsOpen ? { height: panelHeight } : undefined}
      tabIndex={0}
      onKeyDown={onKeyDown}
    >
      {panelIsOpen && (
        <div
          className="content-map__resize-handle"
          onMouseDown={onResizeStart}
        />
      )}
      {!panelIsOpen && (
        <div
          className="content-map__header"
          onClick={() => setPanelIsOpen((v) => !v)}
        >
          {titleBlock}
          <span className="content-map__chevron">▲</span>
        </div>
      )}

      {panelIsOpen && (
        <div className="content-map__body" ref={bodyRef}>
          <table className="content-map__table">
            <colgroup>
              <col className="content-map__col--ref" />
              <col className="content-map__col--id" />
              <col className="content-map__col--id" />
              <col className="content-map__col--match" />
              <col className="content-map__col--name" />
              <col />
            </colgroup>
            <thead>
              <tr className="content-map__thead-row">
                <th className="content-map__th">Ref</th>
                <th className="content-map__th">
                  UAT Asset{" "}
                  <span className="content-map__col-count">
                    ({uatAssetCount})
                  </span>
                </th>
                <th className="content-map__th">
                  Prod Asset{" "}
                  <span className="content-map__col-count">
                    ({prodAssetCount})
                  </span>
                </th>
                <th className="content-map__th">Matching</th>
                <th className="content-map__th">Asset Name</th>
                <th className="content-map__th content-map__th--toolbar">
                  <div className="content-map__thead-toolbar">
                    <div
                      className="content-map__thead-toolbar-left"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <label className="content-map__sort-label">
                        Sort:
                        <select
                          className="content-map__sort-select"
                          value={sortOrder}
                          onChange={(e) =>
                            handleSortChange(e.target.value as SortOrder)
                          }
                          onClick={(e) => e.stopPropagation()}
                        >
                          <option value="visibility">Visibility</option>
                          <option value="az">A–Z</option>
                          <option value="id">ID</option>
                          <option value="updated">Last updated</option>
                        </select>
                      </label>
                      <span className="content-map__th-content-label">
                        Content
                      </span>
                      <input
                        className="content-map__search"
                        type="text"
                        placeholder="Filter by content or path..."
                        value={search}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    <button
                      type="button"
                      className="content-map__thead-toolbar-toggle"
                      onClick={() => setPanelIsOpen(false)}
                      title="Collapse Content Map"
                    >
                      {titleBlock}
                      <span className="content-map__chevron">▼</span>
                    </button>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleSorted.map((entry, index) => {
                const strippedContent = stripHtml(entry.contentBody)
                const isActive = index === activeIndex
                return (
                  <tr
                    key={entry.path}
                    className={`content-map__tr${entry.failed ? " content-map__tr--failed" : ""}${isActive ? " content-map__tr--active" : ""}`}
                    data-content-map-path={entry.path}
                    onClick={() => activateIndex(isActive ? null : index)}
                    style={{ cursor: "pointer" }}
                  >
                    <td className="content-map__td content-map__td--ref">
                      {entry.refId != null ? (
                        <span className="content-map__ref-bubble">
                          {entry.refId}
                        </span>
                      ) : (
                        <span className="content-map__dash">—</span>
                      )}
                    </td>
                    <td className="content-map__td content-map__td--id">
                      {entry.assetId ? (
                        <a
                          className="content-map__link"
                          href={`${adminEditPrefix}${entry.assetId}`}
                          target="_blank"
                          rel="noreferrer"
                          title="Edit"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {entry.assetId}
                        </a>
                      ) : (
                        <span className="content-map__dash">—</span>
                      )}
                    </td>
                    <td className="content-map__td content-map__td--id">
                      {entry.prodAssetId ? (
                        <a
                          className="content-map__link"
                          href={`${adminEditPrefix}${entry.prodAssetId}`}
                          target="_blank"
                          rel="noreferrer"
                          title="Edit"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {entry.prodAssetId}
                        </a>
                      ) : (
                        <span className="content-map__dash">—</span>
                      )}
                    </td>
                    <td className="content-map__td content-map__td--match">
                      {entry.contentLengthMatch === true ? (
                        <span className="content-map__match content-map__match--yes">
                          ✓
                        </span>
                      ) : entry.contentLengthMatch === false ? (
                        <span className="content-map__match content-map__match--no">
                          ✕
                        </span>
                      ) : (
                        <span className="content-map__dash">—</span>
                      )}
                    </td>
                    <td className="content-map__td content-map__td--name">
                      {entry.path}
                    </td>
                    <td
                      className="content-map__td content-map__td--content"
                      title={strippedContent}
                    >
                      {strippedContent ? (
                        highlightText(strippedContent.slice(0, 200), search)
                      ) : (
                        <span className="content-map__dash">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
              {visibleSorted.length === 0 && (
                <tr className="content-map__empty">
                  <td colSpan={6}>
                    {search.trim()
                      ? `No results for "${search}"`
                      : "No content mapped yet"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {hiddenSorted.length > 0 && (
            <div className="content-map__hidden-section">
              <div className="content-map__hidden-header">
                <span className="content-map__hidden-title">
                  Hidden content{" "}
                  <span className="content-map__col-count">
                    ({hiddenSorted.length})
                  </span>
                </span>
                <span className="content-map__hidden-note">
                  This content appears on this page dynamically during specific
                  conditions
                </span>
              </div>
              <table className="content-map__table">
                <colgroup>
                  <col className="content-map__col--ref" />
                  <col className="content-map__col--id" />
                  <col className="content-map__col--id" />
                  <col className="content-map__col--match" />
                  <col className="content-map__col--name" />
                  <col />
                </colgroup>
                <tbody>
                  {hiddenSorted.map((entry) => {
                    const strippedContent = stripHtml(entry.contentBody)
                    return (
                      <tr
                        key={entry.path}
                        className={`content-map__tr content-map__tr--hidden${entry.failed ? " content-map__tr--failed" : ""}`}
                      >
                        <td className="content-map__td content-map__td--ref">
                          <span className="content-map__dash">—</span>
                        </td>
                        <td className="content-map__td content-map__td--id">
                          {entry.assetId ? (
                            <a
                              className="content-map__link"
                              href={`${adminEditPrefix}${entry.assetId}`}
                              target="_blank"
                              rel="noreferrer"
                              title="Edit"
                            >
                              {entry.assetId}
                            </a>
                          ) : (
                            <span className="content-map__dash">—</span>
                          )}
                        </td>
                        <td className="content-map__td content-map__td--id">
                          {entry.prodAssetId ? (
                            <a
                              className="content-map__link"
                              href={`${adminEditPrefix}${entry.prodAssetId}`}
                              target="_blank"
                              rel="noreferrer"
                              title="Edit"
                            >
                              {entry.prodAssetId}
                            </a>
                          ) : (
                            <span className="content-map__dash">—</span>
                          )}
                        </td>
                        <td className="content-map__td content-map__td--match">
                          {entry.contentLengthMatch === true ? (
                            <span className="content-map__match content-map__match--yes">
                              ✓
                            </span>
                          ) : entry.contentLengthMatch === false ? (
                            <span className="content-map__match content-map__match--no">
                              ✕
                            </span>
                          ) : (
                            <span className="content-map__dash">—</span>
                          )}
                        </td>
                        <td className="content-map__td content-map__td--name">
                          <div>{entry.path}</div>
                          {entry.manifestReason ? (
                            <div className="content-map__manifest-reason">
                              {entry.manifestReason}
                            </div>
                          ) : null}
                        </td>
                        <td className="content-map__td content-map__td--content content-map__td--content-full">
                          {strippedContent ? (
                            highlightText(strippedContent, search)
                          ) : (
                            <span className="content-map__dash">—</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
